import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";
import { useProfile } from "@/hooks/useProfile";
import {
  DEFAULT_STATUS_COLORS,
  isAppointmentStatusFinalType,
} from "@/lib/utils/clinic-appointment-status";

export type AppointmentStatusScope = "appointment" | "grooming";
export type AppointmentStatusFinalType = "normal" | "finalizado" | "cancelado";

export interface AppointmentStatus {
  name: string;
  display_name: string;
  description: string | null;
  is_active: boolean;
  sort_order: number;
  scope: AppointmentStatusScope;
  final_type: AppointmentStatusFinalType;
  color: string;
  is_system: boolean;
  created_at: string;
  updated_at: string;
}

export interface AppointmentStatusUpdateInput {
  name?: string;
  display_name: string;
  description?: string | null;
  is_active: boolean;
  sort_order: number;
  final_type: AppointmentStatusFinalType;
  color: string;
  is_system?: boolean;
  replacement_name?: string | null;
}

const DEFAULT_APPOINTMENT_STATUSES: AppointmentStatus[] = [
  {
    name: "agendado",
    display_name: "Agendado",
    description: "Aguardando confirmação.",
    is_active: true,
    sort_order: 10,
    scope: "appointment",
    final_type: "normal",
    color: DEFAULT_STATUS_COLORS.agendado,
    is_system: true,
    created_at: "",
    updated_at: "",
  },
  {
    name: "confirmado",
    display_name: "Confirmado",
    description: "Confirmado com o responsável.",
    is_active: true,
    sort_order: 20,
    scope: "appointment",
    final_type: "normal",
    color: DEFAULT_STATUS_COLORS.confirmado,
    is_system: true,
    created_at: "",
    updated_at: "",
  },
  {
    name: "em_atendimento",
    display_name: "Em atendimento",
    description: "Atendimento em andamento.",
    is_active: true,
    sort_order: 30,
    scope: "appointment",
    final_type: "normal",
    color: DEFAULT_STATUS_COLORS.em_atendimento,
    is_system: true,
    created_at: "",
    updated_at: "",
  },
  {
    name: "finalizado",
    display_name: "Finalizado",
    description: "Atendimento concluído.",
    is_active: true,
    sort_order: 40,
    scope: "appointment",
    final_type: "finalizado",
    color: DEFAULT_STATUS_COLORS.finalizado,
    is_system: true,
    created_at: "",
    updated_at: "",
  },
  {
    name: "cancelado",
    display_name: "Cancelado",
    description: "Agendamento cancelado.",
    is_active: true,
    sort_order: 50,
    scope: "appointment",
    final_type: "cancelado",
    color: DEFAULT_STATUS_COLORS.cancelado,
    is_system: true,
    created_at: "",
    updated_at: "",
  },
  {
    name: "faltou",
    display_name: "Faltou",
    description: "Paciente não compareceu.",
    is_active: true,
    sort_order: 60,
    scope: "appointment",
    final_type: "normal",
    color: DEFAULT_STATUS_COLORS.faltou,
    is_system: true,
    created_at: "",
    updated_at: "",
  },
];

const DEFAULT_GROOMING_STATUSES: AppointmentStatus[] = [
  {
    name: "agendado",
    display_name: "Agendado",
    description: "Aguardando confirmação.",
    is_active: true,
    sort_order: 10,
    scope: "grooming",
    final_type: "normal",
    color: DEFAULT_STATUS_COLORS.agendado,
    is_system: true,
    created_at: "",
    updated_at: "",
  },
  {
    name: "confirmado",
    display_name: "Confirmado",
    description: "Confirmado com o responsável.",
    is_active: true,
    sort_order: 20,
    scope: "grooming",
    final_type: "normal",
    color: DEFAULT_STATUS_COLORS.confirmado,
    is_system: true,
    created_at: "",
    updated_at: "",
  },
  {
    name: "no_banho",
    display_name: "No banho",
    description: "Paciente em banho.",
    is_active: true,
    sort_order: 30,
    scope: "grooming",
    final_type: "normal",
    color: DEFAULT_STATUS_COLORS.no_banho,
    is_system: true,
    created_at: "",
    updated_at: "",
  },
  {
    name: "na_tosa",
    display_name: "Na tosa",
    description: "Paciente em tosa.",
    is_active: true,
    sort_order: 40,
    scope: "grooming",
    final_type: "normal",
    color: DEFAULT_STATUS_COLORS.na_tosa,
    is_system: true,
    created_at: "",
    updated_at: "",
  },
  {
    name: "secando",
    display_name: "Secando",
    description: "Paciente em secagem.",
    is_active: true,
    sort_order: 50,
    scope: "grooming",
    final_type: "normal",
    color: DEFAULT_STATUS_COLORS.secando,
    is_system: true,
    created_at: "",
    updated_at: "",
  },
  {
    name: "finalizado",
    display_name: "Finalizado",
    description: "Serviço concluído.",
    is_active: true,
    sort_order: 60,
    scope: "grooming",
    final_type: "finalizado",
    color: DEFAULT_STATUS_COLORS.finalizado,
    is_system: true,
    created_at: "",
    updated_at: "",
  },
  {
    name: "cancelado",
    display_name: "Cancelado",
    description: "Agendamento cancelado.",
    is_active: true,
    sort_order: 70,
    scope: "grooming",
    final_type: "cancelado",
    color: DEFAULT_STATUS_COLORS.cancelado,
    is_system: true,
    created_at: "",
    updated_at: "",
  },
  {
    name: "faltou",
    display_name: "Faltou",
    description: "Paciente não compareceu.",
    is_active: true,
    sort_order: 80,
    scope: "grooming",
    final_type: "normal",
    color: DEFAULT_STATUS_COLORS.faltou,
    is_system: true,
    created_at: "",
    updated_at: "",
  },
];

const getDefaultStatuses = (scope: AppointmentStatusScope) =>
  scope === "grooming" ? DEFAULT_GROOMING_STATUSES : DEFAULT_APPOINTMENT_STATUSES;

const parseAppointmentStatuses = ({
  data,
  scope,
  includeInactive,
}: {
  data: unknown;
  scope: AppointmentStatusScope;
  includeInactive: boolean;
}): AppointmentStatus[] => {
  const defaults = getDefaultStatuses(scope);
  if (!Array.isArray(data)) {
    return includeInactive ? defaults : defaults.filter((status) => status.is_active);
  }

  const parsed = data
    .map((row): AppointmentStatus | null => {
      if (!row || typeof row !== "object") return null;
      const status = row as Record<string, unknown>;
      const name = typeof status.name === "string" ? status.name : null;
      const displayName = typeof status.display_name === "string" ? status.display_name : null;
      const sortOrder =
        typeof status.sort_order === "number"
          ? status.sort_order
          : typeof status.sort_order === "string"
            ? Number.parseInt(status.sort_order, 10)
            : Number.NaN;

      if (!name || !displayName || Number.isNaN(sortOrder)) return null;

      return {
        name,
        display_name: displayName,
        description: typeof status.description === "string" ? status.description : null,
        is_active: status.is_active !== false,
        sort_order: sortOrder,
        scope:
          status.scope === "grooming" || status.scope === "appointment"
            ? status.scope
            : scope,
        final_type: isAppointmentStatusFinalType(status.final_type) ? status.final_type : "normal",
        color:
          typeof status.color === "string" && /^#[0-9a-f]{6}$/i.test(status.color)
            ? status.color.toLowerCase()
            : DEFAULT_STATUS_COLORS[name] ?? DEFAULT_STATUS_COLORS.agendado,
        is_system: status.is_system === true,
        created_at: typeof status.created_at === "string" ? status.created_at : "",
        updated_at: typeof status.updated_at === "string" ? status.updated_at : "",
      };
    })
    .filter((value): value is AppointmentStatus => Boolean(value))
    .sort((left, right) => left.sort_order - right.sort_order || left.name.localeCompare(right.name));

  if (parsed.length === 0) {
    return includeInactive ? defaults : defaults.filter((status) => status.is_active);
  }

  return includeInactive ? parsed : parsed.filter((status) => status.is_active);
};

export const useAppointmentStatuses = (
  scope: AppointmentStatusScope = "appointment",
  options?: { includeInactive?: boolean },
) => {
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;
  const includeInactive = options?.includeInactive === true;

  return useQuery({
    queryKey: ["appointment-statuses", clinicId, scope, includeInactive],
    queryFn: async () => {
      if (!clinicId) {
        return includeInactive
          ? getDefaultStatuses(scope)
          : getDefaultStatuses(scope).filter((status) => status.is_active);
      }

      const { data, error } = await supabase.rpc("get_clinic_appointment_statuses", {
        p_clinic_id: clinicId,
        p_scope: scope,
        p_include_inactive: includeInactive,
      });

      if (error) throw error;
      return parseAppointmentStatuses({ data, scope, includeInactive });
    },
    enabled: profile !== undefined,
  });
};

export const getStatusDisplayName = (statusName: string, statuses?: AppointmentStatus[]): string => {
  if (!statuses) return statusName;
  const status = statuses.find((item) => item.name === statusName);
  return status?.display_name || statusName;
};

export const useUpdateClinicAppointmentStatuses = () => {
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;

  return useMutation({
    mutationFn: async ({
      scope,
      statuses,
    }: {
      scope: AppointmentStatusScope;
      statuses: AppointmentStatusUpdateInput[];
    }): Promise<AppointmentStatus[]> => {
      if (!clinicId) {
        throw new Error("clinic_required");
      }

      const payload = statuses.map((status) => ({
        name: status.name ?? "",
        display_name: status.display_name,
        description: status.description ?? null,
        is_active: status.is_active,
        sort_order: status.sort_order,
        final_type: status.final_type,
        color: status.color,
        is_system: status.is_system === true,
        replacement_name: status.replacement_name ?? null,
      }));

      const { data, error } = await supabase.rpc("set_clinic_appointment_statuses", {
        p_clinic_id: clinicId,
        p_scope: scope,
        p_statuses: payload as unknown as Json,
      });

      if (error) throw error;
      return parseAppointmentStatuses({ data, scope, includeInactive: true });
    },
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ["appointment-statuses", clinicId, variables.scope],
      });
    },
  });
};
